import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  MessageSquareText,
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  Send,
  Plus,
  Pencil,
  Trash2,
  Building2,
  Phone,
  Zap,
  Shield,
  ImageIcon,
  Upload,
  Camera,
  FileText,
  AlertCircle,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CATEGORY_LABELS } from "@shared/types";
import type { TransactionCategory } from "@shared/types";
import { useLanguage, CATEGORY_LABELS_AR } from "@/contexts/LanguageContext";
import { ScrollArea } from "@/components/ui/scroll-area";

export default function SMSParser() {
  const { t, language, isRTL } = useLanguage();
  const [, setLocation] = useLocation();
  const [smsText, setSmsText] = useState("");
  const [mode, setMode] = useState<"sms" | "image" | "pdf">("pdf");
  const [successPopup, setSuccessPopup] = useState<{ open: boolean; title: string; description: string } | null>(null);

  const [balanceChainConflict, setBalanceChainConflict] = useState<{
    open: boolean;
    message: string;
    breaks: Array<{ transactionDate: number; breakSize: number; expectedBalance: number; actualBalance: number }>;
    totalBreaks: number;
    pendingCount: number;
    base64: string;
    fileName: string;
    bankSource?: string;
  } | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfProcessing, setPdfProcessing] = useState(false);
  const [pdfResult, setPdfResult] = useState<any>(null);
  const [pendingPdfImport, setPendingPdfImport] = useState<{ fileHash: string; fileName: string } | null>(null);
  const [pdfFailure, setPdfFailure] = useState<{ kind: "zero_rows" | "transport" | "password_required" | "processing" | "not_bank_statement"; expectedRows?: number; pages?: number; message?: string; evidence?: string[] } | null>(null);
  const [pdfPassword, setPdfPassword] = useState("");
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [detectedBank, setDetectedBank] = useState<string | null>(null);
  const [selectedBankSource, setSelectedBankSource] = useState<string | null>(null);

  // Validation mismatch state
  const [validationMismatch, setValidationMismatch] = useState<{
    open: boolean;
    parsedCount: number;
    totalPages: number;
    expectedDebits: number;
    actualDebits: number;
    expectedCredits: number;
    actualCredits: number;
    missingDebitAmount: number;
    missingCreditAmount: number;
    summary: { previousBalance: number | null; totalDebits: number | null; totalCredits: number | null; newBalance: number | null } | null;
    retryCount: number;
  } | null>(null);
  const [pageShortfall, setPageShortfall] = useState<{
    open: boolean;
    pagesProcessed: number;
    rowsFound: number;
    rowsExpected: number;
    shortfalls: Array<{ pageNumber: number; expectedRows: number; extractedRows: number; shortfall: number }>;
    valueUnaccounted: { debits: number; credits: number };
    base64: string;
    fileName: string;
    bankSource?: string;
    retryCount: number;
  } | null>(null);

  // Image upload state (multi-image)
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [imageProcessing, setImageProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState<{ current: number; total: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Bank sender form state
  const [showAddSender, setShowAddSender] = useState(false);
  const [editingSender, setEditingSender] = useState<{ id: number; senderNumber: string; bankName: string; bankCode: string | null } | null>(null);
  const [senderNumber, setSenderNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankCode, setBankCode] = useState("");

  const utils = trpc.useUtils();

  // Queries
  const { data: bankSenders, isLoading: sendersLoading } = trpc.bankSender.list.useQuery();
  const { data: activeSenders } = trpc.bankSender.listActive.useQuery();
  const pdfImportStatus = trpc.sms.pdfImportStatus.useQuery(
    { fileHash: pendingPdfImport?.fileHash ?? "0".repeat(64) },
    { enabled: Boolean(pendingPdfImport), refetchInterval: (query) => query.state.data?.status === "processing" ? 2_000 : false },
  );

  useEffect(() => {
    const stored = window.localStorage.getItem("mizano.pendingPdfImport");
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      if (typeof parsed.fileHash === "string" && /^[a-f0-9]{64}$/i.test(parsed.fileHash) && typeof parsed.fileName === "string") {
        setPendingPdfImport(parsed);
      }
    } catch {
      window.localStorage.removeItem("mizano.pendingPdfImport");
    }
  }, []);

  useEffect(() => {
    const status = pdfImportStatus.data;
    if (!pendingPdfImport || !status || status.status === "processing") return;
    setPdfProcessing(false);
    if (status.status === "completed") {
      utils.transaction.list.invalidate();
      utils.transaction.summary.invalidate();
      utils.transaction.latestBalance.invalidate();
      setSuccessPopup({
        open: true,
        title: t("success.uploadComplete"),
        description: t("sms.pdfCompletedAfterReconnect").replace("{count}", String(status.transactionCount)),
      });
    } else if (status.documentError) {
      setPdfFailure({ kind: "transport", message: status.documentError });
    }
    window.localStorage.removeItem("mizano.pendingPdfImport");
    setPendingPdfImport(null);
  }, [pendingPdfImport, pdfImportStatus.data, t, utils.transaction.list, utils.transaction.summary, utils.transaction.latestBalance]);

  // Mutations
  const createSender = trpc.bankSender.create.useMutation({
    onSuccess: () => {
      toast.success(language === "ar" ? "تمت إضافة مرسل البنك!" : "Bank sender added!");
      resetSenderForm();
      utils.bankSender.list.invalidate();
      utils.bankSender.listActive.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const updateSenderMut = trpc.bankSender.update.useMutation({
    onSuccess: () => {
      toast.success(language === "ar" ? "تم تحديث مرسل البنك!" : "Bank sender updated!");
      resetSenderForm();
      utils.bankSender.list.invalidate();
      utils.bankSender.listActive.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteSender = trpc.bankSender.delete.useMutation({
    onSuccess: () => {
      toast.success(language === "ar" ? "تمت إزالة مرسل البنك!" : "Bank sender removed!");
      utils.bankSender.list.invalidate();
      utils.bankSender.listActive.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const parseSingle = trpc.sms.parse.useMutation({
    onSuccess: (data) => {
      if (data.success) {
        setSmsText("");
        setDetectedBank(null);
        setSelectedBankSource(null);
        utils.transaction.list.invalidate();
        utils.transaction.summary.invalidate();
        utils.transaction.latestBalance.invalidate();
        setSuccessPopup({
          open: true,
          title: t("success.uploadComplete"),
          description: t("success.singleParsed"),
        });
      } else {
        if (data.duplicate) {
          toast.warning(language === "ar" ? "رسالة مكررة تم تخطيها" : "Duplicate SMS detected, skipped.");
        } else {
          toast.error(data.error ?? (language === "ar" ? "فشل تحليل الرسالة" : "Failed to parse SMS"));
        }
      }
    },
    onError: (err) => toast.error(err.message),
  });

  const parseBulk = trpc.sms.parseBulk.useMutation({
    onSuccess: (data) => {
      if (data.success) {
        if (data.failedCount === 0) {
          setSmsText("");
          setDetectedBank(null);
          setSelectedBankSource(null);
        }
        utils.transaction.list.invalidate();
        utils.transaction.summary.invalidate();
        utils.transaction.latestBalance.invalidate();
        setSuccessPopup({
          open: true,
          title: t("success.uploadComplete"),
          description: t("success.bulkParsed")
            .replace("{parsed}", String(data.parsedCount))
            .replace("{failed}", String(data.failedCount))
            .replace("{duplicates}", String(data.duplicateCount)),
        });
      }
    },
    onError: (err) => toast.error(err.message),
  });

  const parseImage = trpc.sms.parseImage.useMutation({
    onSuccess: (data) => {
      setImageProcessing(false);
      if (data.success) {
        if (data.bankSender) {
          setDetectedBank(data.bankSender);
        }
        utils.transaction.list.invalidate();
        utils.transaction.summary.invalidate();
        utils.transaction.latestBalance.invalidate();
        setSuccessPopup({
          open: true,
          title: t("success.uploadComplete"),
          description: t("success.imageParsed")
            .replace("{parsed}", String(data.parsedCount))
            .replace("{failed}", String(data.failedCount))
            .replace("{duplicates}", String(data.duplicateCount)),
        });
      } else {
        toast.error(data.error ?? (language === "ar" ? "فشل معالجة الصورة" : "Failed to process image"));
      }
    },
    onError: (err) => {
      setImageProcessing(false);
      toast.error(err.message);
    },
  });

  const uploadImage = trpc.sms.uploadImage.useMutation();

  const parsePDF = trpc.sms.parsePDF.useMutation({
    onSuccess: (data) => {
      setPdfProcessing(false);
      setPdfResult(data);
      if (data.success) {
        if ((data as any).reconciledImport) {
          setPdfFailure(null);
          window.localStorage.removeItem("mizano.pendingPdfImport");
          setPendingPdfImport(null);
          utils.transaction.list.invalidate();
          utils.transaction.summary.invalidate();
          utils.transaction.latestBalance.invalidate();
          setSuccessPopup({ open: true, title: t("success.uploadComplete"), description: t("sms.pdfAlreadyImported") });
          return;
        }
        // Check for balance chain conflict BEFORE showing success
        if ((data as any).balanceChainConflict) {
          const conflict = (data as any).balanceChainConflict;
          // Store the current file data for re-submission with resolution
          const reader = new FileReader();
          if (pdfFile) {
            reader.onload = (ev) => {
              const dataUrl = ev.target?.result as string;
              const base64 = dataUrl.split(",")[1];
              setBalanceChainConflict({
                open: true,
                message: conflict.message,
                breaks: conflict.breaks,
                totalBreaks: conflict.totalBreaks,
                pendingCount: conflict.pendingCount,
                base64,
                fileName: pdfFile.name,
                bankSource: selectedBankSource ?? detectedBank ?? undefined,
              });
            };
            reader.readAsDataURL(pdfFile);
          }
          return;
        }

        utils.transaction.list.invalidate();
        utils.transaction.summary.invalidate();
        utils.transaction.latestBalance.invalidate();
        window.localStorage.removeItem("mizano.pendingPdfImport");
        setPendingPdfImport(null);
        setPdfFailure(null);
        if (data.parsedCount === 0 && data.duplicateCount > 0) {
          setSuccessPopup({
            open: true,
            title: t("success.uploadComplete"),
            description: t("success.pdfAllDuplicates")
              .replace("{total}", String(data.totalExtracted ?? data.duplicateCount))
              .replace("{pages}", String(data.totalPages)),
          });
        } else {
          setSuccessPopup({
            open: true,
            title: t("success.uploadComplete"),
            description: t("success.pdfParsed")
              .replace("{parsed}", String(data.parsedCount))
              .replace("{pages}", String(data.totalPages))
              .replace("{duplicates}", String(data.duplicateCount)),
          });
        }
        if ((data as any).protection === "recovered_empty_password" || (data as any).protection === "recovered_password") {
          toast.info(t("sms.pdfProtectedRecovered"));
        }
        // Warn if account number could not be verified from the PDF
        if (data.isUnverifiedAccount) {
          toast.warning(
            language === "ar"
              ? `⚠️ لم يتم التعرف على رقم الحساب. تم إنشاء حساب غير مؤكد: ${data.accountKey}. يرجى تسميته في إعدادات الحساب.`
              : `⚠️ Account number could not be extracted. An unverified account was created: ${data.accountKey}. Please name it in Account Settings.`
          );
        }
      } else if ((data as any).processingImport || (data as any).failureCode === "PDF_IMPORT_PROCESSING") {
        // A claimed same-hash import is active. This is neither an extraction failure nor a zero-row result.
        setPdfFailure({ kind: "processing", message: data.error });
      } else if ((data as any).incompleteExtraction) {
        const incomplete = (data as any).incompleteExtraction;
        if (pdfFile) {
          const reader = new FileReader();
          reader.onload = (ev) => {
            const dataUrl = ev.target?.result as string;
            setPageShortfall({
              open: true,
              pagesProcessed: incomplete.pagesProcessed,
              rowsFound: incomplete.rowsFound,
              rowsExpected: incomplete.rowsExpected,
              shortfalls: incomplete.shortfalls,
              valueUnaccounted: incomplete.valueUnaccounted,
              base64: dataUrl.split(",")[1],
              fileName: pdfFile.name,
              bankSource: selectedBankSource ?? detectedBank ?? undefined,
              retryCount: (data as any).retryCount ?? 0,
            });
          };
          reader.readAsDataURL(pdfFile);
        }
      } else if ((data as any).validationError) {
        // Control-total mismatch - show detailed error with option to force import
        const ve = (data as any).validationError;
        const summary = (data as any).summary;
        setValidationMismatch({
          open: true,
          parsedCount: data.parsedCount ?? 0,
          totalPages: data.totalPages ?? 0,
          expectedDebits: ve.expectedDebits,
          actualDebits: ve.actualDebits,
          expectedCredits: ve.expectedCredits,
          actualCredits: ve.actualCredits,
          missingDebitAmount: ve.missingDebitAmount,
          missingCreditAmount: ve.missingCreditAmount,
          summary: summary ?? null,
          retryCount: (data as any).retryCount ?? 0,
        });
      } else {
        if ((data as any).failureCode === "PDF_DOCUMENT_NOT_BANK_STATEMENT") {
          window.localStorage.removeItem("mizano.pendingPdfImport");
          setPendingPdfImport(null);
          setPdfFailure({ kind: "not_bank_statement", message: data.error, evidence: (data as any).documentClassification?.evidence ?? [] });
          return;
        }
        if ((data as any).failureCode === "PDF_PASSWORD_REQUIRED") {
          setPdfFailure({ kind: "password_required", message: data.error });
          return;
        }
        const expectedRows = Array.isArray((data as any).pageRows) ? (data as any).pageRows.reduce((sum: number, page: any) => sum + (page.expectedRows ?? 0), 0) : 0;
        setPdfFailure({ kind: "zero_rows", expectedRows, pages: data.totalPages ?? 0, message: data.error });
      }
    },
    onError: (err) => {
      setPdfProcessing(false);
      setPdfFailure({ kind: "transport", message: err.message });
    },
  });

  const startPdfImport = async () => {
    if (!pdfFile) {
      toast.error(language === "ar" ? "الرجاء اختيار ملف PDF" : "Please select a PDF file");
      return;
    }
    setPdfProcessing(true);
    setPdfResult(null);
    setPdfFailure(null);
    try {
      const [buffer, base64] = await Promise.all([
        pdfFile.arrayBuffer(),
        new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = (ev) => resolve((ev.target?.result as string).split(",")[1]);
          reader.readAsDataURL(pdfFile);
        }),
      ]);
      const digest = await window.crypto.subtle.digest("SHA-256", buffer);
      const fileHash = Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, "0")).join("");
      const pending = { fileHash, fileName: pdfFile.name };
      window.localStorage.setItem("mizano.pendingPdfImport", JSON.stringify(pending));
      setPendingPdfImport(pending);
      parsePDF.mutate({ base64, fileName: pdfFile.name, bankSource: selectedBankSource ?? detectedBank ?? undefined, pdfPassword: pdfPassword || undefined });
    } catch (err: any) {
      setPdfProcessing(false);
      setPdfFailure({ kind: "transport", message: err?.message ?? "Failed to read file" });
    }
  };

  const deleteAll = trpc.sms.deleteAll.useMutation({
    onSuccess: (data) => {
      utils.transaction.list.invalidate();
      utils.transaction.summary.invalidate();
      utils.transaction.latestBalance.invalidate();
      utils.transaction.reconciliationStatus.invalidate();
      setSuccessPopup({
        open: true,
        title: t("success.deleteComplete"),
        description: t("success.deleteDesc"),
      });
    },
    onError: (err) => toast.error(err.message),
  });

  const reportError = trpc.sms.reportError.useMutation({
    onSuccess: () => toast.success(language === "ar" ? "تم الإبلاغ عن الخطأ. شكراً!" : "Error reported to admin. Thank you!"),
    onError: (err) => toast.error(err.message),
  });

  // Auto-detect bank from pasted text
  const detectBankFromText = useCallback(
    (text: string) => {
      if (!activeSenders || activeSenders.length === 0 || !text.trim()) {
        setDetectedBank(null);
        return;
      }
      const textLower = text.toLowerCase();

      // Arabic bank name aliases for client-side detection
      const ARABIC_BANK_ALIASES: Record<string, string[]> = {
        "kfh": ["بيت التمويل", "بيت التمويل الكويتي"],
        "nbk": ["بنك الكويت الوطني", "الوطني"],
        "boubyan": ["بنك بوبيان", "بوبيان"],
        "abk": ["البنك الأهلي", "الأهلي الكويتي"],
        "cbk": ["البنك التجاري", "التجاري الكويتي"],
        "gbk": ["بنك الخليج", "الخليج"],
        "burgan": ["بنك برقان", "برقان"],
        "warba": ["بنك وربة", "وربة"],
      };

      for (const sender of activeSenders) {
        const senderLower = sender.senderNumber.toLowerCase();
        const bankLower = sender.bankName.toLowerCase();
        const bankCodeLower = ((sender as any).bankCode ?? "").toLowerCase();

        // Direct match: sender number, bank name, or bank code
        if (textLower.includes(senderLower) || textLower.includes(bankLower)) {
          setDetectedBank(sender.bankName);
          setSelectedBankSource(sender.bankName);
          return;
        }
        if (bankCodeLower && textLower.includes(bankCodeLower)) {
          setDetectedBank(sender.bankName);
          setSelectedBankSource(sender.bankName);
          return;
        }

        // Arabic alias matching
        for (const [aliasKey, aliases] of Object.entries(ARABIC_BANK_ALIASES)) {
          const matchesBank = bankLower.includes(aliasKey) || bankCodeLower === aliasKey || senderLower.includes(aliasKey);
          if (matchesBank) {
            for (const alias of aliases) {
              if (text.includes(alias) || textLower.includes(alias.toLowerCase())) {
                setDetectedBank(sender.bankName);
                setSelectedBankSource(sender.bankName);
                return;
              }
            }
          }
        }
      }

      // Fallback: detect KFH Arabic pattern by content
      if (/حسابك\s*xx\d+/.test(text) && /د\.ك/.test(text)) {
        for (const sender of activeSenders) {
          const bankLower = sender.bankName.toLowerCase();
          const bankCodeLower = ((sender as any).bankCode ?? "").toLowerCase();
          if (bankLower.includes("kfh") || bankLower.includes("تمويل") || bankCodeLower === "kfh") {
            setDetectedBank(sender.bankName);
            setSelectedBankSource(sender.bankName);
            return;
          }
        }
      }

      setDetectedBank(null);
    },
    [activeSenders]
  );

  useEffect(() => {
    if (smsText.trim()) {
      detectBankFromText(smsText);
    } else {
      setDetectedBank(null);
      setSelectedBankSource(null);
    }
  }, [smsText, detectBankFromText]);

  const resetSenderForm = () => {
    setShowAddSender(false);
    setEditingSender(null);
    setSenderNumber("");
    setBankName("");
    setBankCode("");
  };

  const handleSaveSender = () => {
    if (!senderNumber.trim() || !bankName.trim()) {
      toast.error(language === "ar" ? "رقم المرسل واسم البنك مطلوبان" : "Sender number and bank name are required");
      return;
    }
    if (editingSender) {
      updateSenderMut.mutate({
        id: editingSender.id,
        senderNumber: senderNumber.trim(),
        bankName: bankName.trim(),
        bankCode: bankCode.trim() || undefined,
      });
    } else {
      createSender.mutate({
        senderNumber: senderNumber.trim(),
        bankName: bankName.trim(),
        bankCode: bankCode.trim() || undefined,
      });
    }
  };

  const handleEditSender = (sender: { id: number; senderNumber: string; bankName: string; bankCode: string | null }) => {
    setEditingSender(sender);
    setSenderNumber(sender.senderNumber);
    setBankName(sender.bankName);
    setBankCode(sender.bankCode ?? "");
    setShowAddSender(true);
  };

  const handleToggleSender = (id: number, currentActive: number) => {
    updateSenderMut.mutate({ id, isActive: currentActive === 1 ? 0 : 1 });
  };

  const handleParse = () => {
    if (!smsText.trim()) {
      toast.error(language === "ar" ? "الرجاء إدخال نص الرسالة" : "Please enter SMS text to parse");
      return;
    }
    const bankSource = selectedBankSource ?? detectedBank ?? undefined;
    // Auto-detect single vs bulk: split by double newlines
    const segments = smsText.split(/\n\s*\n/).filter((s) => s.trim().length > 0);
    if (segments.length > 1) {
      parseBulk.mutate({ text: smsText, bankSource });
    } else {
      parseSingle.mutate({ text: smsText, bankSource });
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    const maxImages = 20;
    const remaining = maxImages - selectedImages.length;
    if (remaining <= 0) {
      toast.error(language === "ar" ? `الحد الأقصى ${maxImages} صورة` : `Maximum ${maxImages} images allowed`);
      return;
    }

    const validFiles: File[] = [];
    for (const file of files.slice(0, remaining)) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(language === "ar" ? `${file.name}: حجم الملف يتجاوز 10 ميجابايت` : `${file.name}: File size exceeds 10MB limit`);
        continue;
      }
      if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
        toast.error(language === "ar" ? `${file.name}: ليس ملف صورة أو PDF` : `${file.name}: Not an image or PDF file`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    // Generate previews for new files
    const newPreviews: Promise<string>[] = validFiles.map(
      (file) =>
        new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = (ev) => resolve(ev.target?.result as string);
          reader.readAsDataURL(file);
        })
    );

    Promise.all(newPreviews).then((previews) => {
      setSelectedImages((prev) => [...prev, ...validFiles]);
      setImagePreviews((prev) => [...prev, ...previews]);
    });
  };

  const removeImage = (index: number) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const clearAllImages = () => {
    setSelectedImages([]);
    setImagePreviews([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleImageUploadAndParse = async () => {
    if (selectedImages.length === 0) {
      toast.error(language === "ar" ? "الرجاء اختيار صورة واحدة على الأقل" : "Please select at least one image");
      return;
    }

    setImageProcessing(true);
    setProcessingProgress({ current: 0, total: selectedImages.length });

    try {
      // Upload all images to S3
      const uploadedUrls: string[] = [];
      for (let i = 0; i < selectedImages.length; i++) {
        setProcessingProgress({ current: i + 1, total: selectedImages.length });
        const file = selectedImages[i];
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = (ev) => {
            const dataUrl = ev.target?.result as string;
            resolve(dataUrl.split(",")[1]);
          };
          reader.readAsDataURL(file);
        });

        const { url } = await uploadImage.mutateAsync({
          base64,
          mimeType: file.type,
          fileName: file.name,
        });
        uploadedUrls.push(url);
      }

      // Parse all images at once
      setProcessingProgress(null);
      const bankSource = selectedBankSource ?? detectedBank ?? undefined;
      parseImage.mutate({ imageUrls: uploadedUrls, bankSource });
    } catch (err: any) {
      setImageProcessing(false);
      setProcessingProgress(null);
      toast.error(err.message || (language === "ar" ? "فشل رفع الصور" : "Failed to upload images"));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files ?? []).filter((f) => f.type.startsWith("image/"));
    if (files.length > 0) {
      // Simulate file input change event with multiple files
      const dt = new DataTransfer();
      files.forEach((f) => dt.items.add(f));
      const fakeEvent = { target: { files: dt.files } } as any;
      handleImageSelect(fakeEvent);
    }
  };

  const isLoading = parseSingle.isPending || parseBulk.isPending;
  const lastResult = parseSingle.data;
  const bulkResult = parseBulk.data;
  const imageResult = parseImage.data;

  const getCategoryLabel = (cat: string) => {
    const c = cat === "bills_utilities" ? "bills" : cat;
    if (language === "ar") {
      return CATEGORY_LABELS_AR[c] ?? CATEGORY_LABELS[c as TransactionCategory] ?? c.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
    }
    return CATEGORY_LABELS[c as TransactionCategory] ?? c.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="space-y-6" dir={isRTL ? "rtl" : "ltr"}>
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{t("sms.title")}</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {t("sms.subtitle")}
        </p>
      </div>

      {/* Upload Transaction Data -- left accent border like Budgets tracking cards */}
      <Card className="border-l-4 border-l-primary">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Upload className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">{t("sms.inputTitle")}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs value={mode} onValueChange={(v) => setMode(v as "sms" | "image" | "pdf")}>
            <TabsList className="grid w-full grid-cols-3 min-h-11">
              <TabsTrigger value="pdf" className="gap-1.5 text-xs sm:text-sm px-1 sm:px-3">
                <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="truncate hidden sm:inline">{t("sms.statementUploader")}</span>
                <span className="truncate sm:hidden">{language === "ar" ? "كشف" : "Statement"}</span>
              </TabsTrigger>
              <TabsTrigger value="sms" className="gap-1.5 text-xs sm:text-sm px-2 sm:px-3">
                <MessageSquareText className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="truncate">{t("sms.sms")}</span>
              </TabsTrigger>
              <TabsTrigger value="image" className="gap-1.5 text-xs sm:text-sm px-2 sm:px-3">
                <Camera className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="truncate">{t("sms.image")}</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="sms" className="space-y-4 mt-4">
              <Textarea
                placeholder={t("sms.pasteSms")}
                value={smsText}
                onChange={(e) => setSmsText(e.target.value)}
                rows={6}
                className="font-mono text-sm"
                dir="auto"
              />
              {detectedBank && (
                <div className="flex items-center gap-2">
                  <Zap className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs text-primary font-medium">
                    {t("sms.detectedBank")} {detectedBank}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleParse}
                  disabled={isLoading || !smsText.trim()}
                  className="gap-2"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {isLoading ? t("sms.parsing") : t("sms.parseAndSave")}
                </Button>
                {smsText.trim() && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      reportError.mutate({ rawMessage: smsText, errorDetails: "User requested manual review" });
                    }}
                    className="text-muted-foreground gap-1"
                  >
                    <AlertTriangle className="h-3 w-3" />
                    {t("sms.reportIssue")}
                  </Button>
                )}
              </div>
            </TabsContent>

            <TabsContent value="image" className="space-y-4 mt-4">
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t("sms.imageDesc")}
                </p>

                {/* Drop zone */}
                <div
                  className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                >
                  {imagePreviews.length > 0 ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {imagePreviews.map((preview, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={preview}
                              alt={`Screenshot ${index + 1}`}
                              className="w-full h-32 object-cover rounded-lg shadow-sm"
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeImage(index);
                              }}
                              className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-xs font-bold"
                            >
                              ×
                            </button>
                            <p className="text-[10px] text-muted-foreground mt-1 truncate">
                              {selectedImages[index]?.name}
                            </p>
                          </div>
                        ))}
                        {/* Add more button */}
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            fileInputRef.current?.click();
                          }}
                          className="h-32 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-1 hover:border-primary/50 transition-colors cursor-pointer"
                        >
                          <Plus className="h-5 w-5 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            {language === "ar" ? "إضافة صور" : "Add more"}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">
                          {language === "ar"
                            ? `${selectedImages.length} صورة مختارة`
                            : `${selectedImages.length} image${selectedImages.length > 1 ? "s" : ""} selected`}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            clearAllImages();
                          }}
                        >
                          {language === "ar" ? "مسح الكل" : "Clear all"}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="h-12 w-12 mx-auto rounded-full bg-muted flex items-center justify-center">
                        <ImageIcon className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <p className="text-sm text-muted-foreground">{t("sms.dragDrop")}</p>
                      <p className="text-xs text-muted-foreground">{t("sms.supportedTypes")}</p>
                      <p className="text-xs text-muted-foreground">{t("sms.maxSize")}</p>
                    </div>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  className="hidden"
                  onChange={handleImageSelect}
                  multiple
                />

                <div className="flex items-center gap-3">
                  <Button
                    onClick={handleImageUploadAndParse}
                    disabled={selectedImages.length === 0 || imageProcessing}
                    className="gap-2"
                  >
                    {imageProcessing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {imageProcessing
                      ? processingProgress
                        ? (language === "ar"
                            ? `جاري رفع ${processingProgress.current}/${processingProgress.total}...`
                            : `Uploading ${processingProgress.current}/${processingProgress.total}...`)
                        : (uploadImage.isPending ? t("sms.uploading") : t("sms.processing"))
                      : (language === "ar"
                          ? `رفع وتحليل${selectedImages.length > 1 ? ` (${selectedImages.length})` : ""}`
                          : `Upload & Parse${selectedImages.length > 1 ? ` (${selectedImages.length})` : ""}`)}
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="pdf" className="space-y-4 mt-4">
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {language === "ar"
                    ? "ارفع كشف حساب بنكي بصيغة PDF. سيقوم التطبيق بقراءة جميع المعاملات واستخراجها تلقائياً."
                    : "Upload a PDF bank statement. The app will read all transaction rows and extract them automatically."}
                </p>

                {/* PDF drop zone */}
                <div
                  className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => pdfInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files?.[0];
                    if (file && file.type === "application/pdf") {
                      if (file.size > 10 * 1024 * 1024) {
                        toast.error(language === "ar" ? "حجم الملف يتجاوز 10 ميجابايت" : "File size exceeds 10MB limit");
                        return;
                      }
                      setPdfFile(file);
                      setPdfResult(null);
                    } else {
                      toast.error(language === "ar" ? "الرجاء رفع ملف PDF" : "Please upload a PDF file");
                    }
                  }}
                >
                  {pdfFile ? (
                    <div className="space-y-2">
                      <div className="h-12 w-12 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                        <FileText className="h-6 w-6 text-primary" />
                      </div>
                      <p className="text-sm font-medium text-foreground">{pdfFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(pdfFile.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPdfFile(null);
                          setPdfResult(null);
                          if (pdfInputRef.current) pdfInputRef.current.value = "";
                        }}
                      >
                        {language === "ar" ? "إزالة" : "Remove"}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="h-12 w-12 mx-auto rounded-full bg-muted flex items-center justify-center">
                        <FileText className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {language === "ar" ? "اسحب وأفلت ملف PDF هنا، أو انقر للتصفح" : "Drag & drop a PDF file here, or click to browse"}
                      </p>
                      <p className="text-xs text-muted-foreground">{t("sms.maxSize")}</p>
                    </div>
                  )}
                </div>

                <input
                  ref={pdfInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 10 * 1024 * 1024) {
                        toast.error(language === "ar" ? "حجم الملف يتجاوز 10 ميجابايت" : "File size exceeds 10MB limit");
                        return;
                      }
                      setPdfFile(file);
                      setPdfResult(null);
                    }
                  }}
                />

                <Button
                  onClick={startPdfImport}
                  disabled={!pdfFile || pdfProcessing}
                  className="gap-2"
                >
                  {pdfProcessing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  {pdfProcessing ? t("sms.pdfProcessing") : (language === "ar" ? "رفع وتحليل كشف الحساب" : "Upload & Parse Statement")}
                </Button>

                {(pdfFailure || (pendingPdfImport && pdfImportStatus.data?.status === "processing")) && (
                  <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm" role="status">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                      <div className="min-w-0 space-y-1">
                        <p className="font-medium">
                          {pdfFailure?.kind === "not_bank_statement" ? t("sms.pdfNotBankStatementTitle") : pdfFailure?.kind === "password_required" ? t("sms.pdfPasswordTitle") : pdfFailure?.kind === "zero_rows" ? t("sms.pdfZeroRowsTitle") : pdfFailure?.kind === "processing" || (pendingPdfImport && pdfImportStatus.data?.status === "processing") ? t("sms.pdfReconcileTitle") : t("sms.pdfTransportTitle")}
                        </p>
                        <p className="text-muted-foreground">
                          {pdfFailure?.kind === "not_bank_statement"
                            ? t("sms.pdfNotBankStatementDescription")
                            : pdfFailure?.kind === "password_required"
                            ? t("sms.pdfPasswordDescription")
                            : pdfFailure?.kind === "zero_rows"
                            ? t("sms.pdfZeroRowsDescription").replace("{expected}", String(pdfFailure.expectedRows ?? 0)).replace("{pages}", String(pdfFailure.pages ?? 0))
                            : pdfFailure?.kind === "processing" || (pendingPdfImport && pdfImportStatus.data?.status === "processing")
                              ? t("sms.pdfReconcileDescription")
                              : t("sms.pdfTransportDescription")}
                        </p>
                        {pdfFailure?.message && <p className="break-words text-xs text-muted-foreground">{pdfFailure.message}</p>}
                        {pdfFailure?.kind === "not_bank_statement" && pdfFailure.evidence?.length ? <div className="pt-1 text-xs text-muted-foreground"><p className="font-medium">{t("sms.pdfClassificationEvidence")}</p><ul className="mt-1 list-disc space-y-1 ps-5">{pdfFailure.evidence.map((item) => <li key={item}>{item}</li>)}</ul></div> : null}
                        {pdfFailure?.kind === "password_required" && (
                          <label className="block pt-2 text-sm font-medium">
                            {t("sms.pdfPasswordLabel")}
                            <input className="mt-1 min-h-11 w-full rounded-md border border-border bg-background px-3" type="password" autoComplete="current-password" value={pdfPassword} onChange={(event) => setPdfPassword(event.target.value)} />
                          </label>
                        )}
                        <div className="flex flex-wrap gap-2 pt-2">
                          <Button type="button" size="sm" variant="outline" onClick={pdfFailure?.kind === "not_bank_statement" ? () => pdfInputRef.current?.click() : startPdfImport} disabled={!pdfFile || pdfProcessing || (pdfFailure?.kind === "password_required" && !pdfPassword)}>
                            {pdfFailure?.kind === "not_bank_statement" ? t("sms.pdfChooseAnother") : t("sms.pdfRetry")}
                          </Button>
                          {pendingPdfImport && <Button type="button" size="sm" variant="ghost" onClick={() => pdfImportStatus.refetch()}>{t("sms.pdfRefreshImports")}</Button>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* HIDDEN: Bank SMS Numbers Registry - will re-enable for app store launch */}
      {false && <div className="rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="h-7 w-7 rounded-lg bg-primary/20 flex items-center justify-center">
                <Building2 className="h-4 w-4 text-primary" />
              </div>
              <h2 className="text-sm font-semibold text-foreground">{t("sms.registeredBanks")}</h2>
            </div>
            <p className="text-[10px] sm:text-xs text-muted-foreground">{t("sms.registeredBanksDesc")}</p>
          </div>
            <Dialog open={showAddSender} onOpenChange={(open) => { if (!open) resetSenderForm(); else setShowAddSender(true); }}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-1" onClick={() => setShowAddSender(true)}>
                  <Plus className="h-3.5 w-3.5" />
                  {t("sms.addBank")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingSender ? t("sms.editBank") : t("sms.addBankTitle")}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="senderNumber">{t("sms.senderNumber")}</Label>
                    <div className="relative">
                      <Phone className={`absolute ${isRTL ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground`} />
                      <Input
                        id="senderNumber"
                        placeholder={t("sms.senderNumberPlaceholder")}
                        value={senderNumber}
                        onChange={(e) => setSenderNumber(e.target.value)}
                        className={isRTL ? "pr-10" : "pl-10"}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t("sms.senderNumberDesc")}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bankName">{t("sms.bankName")}</Label>
                    <div className="relative">
                      <Building2 className={`absolute ${isRTL ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground`} />
                      <Input
                        id="bankName"
                        placeholder={t("sms.bankNamePlaceholder")}
                        value={bankName}
                        onChange={(e) => setBankName(e.target.value)}
                        className={isRTL ? "pr-10" : "pl-10"}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bankCode">{t("sms.bankCode")}</Label>
                    <Input
                      id="bankCode"
                      placeholder={t("sms.bankCodePlaceholder")}
                      value={bankCode}
                      onChange={(e) => setBankCode(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t("sms.bankCodeDesc")}
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="ghost" onClick={resetSenderForm}>{t("sms.cancel")}</Button>
                  </DialogClose>
                  <Button onClick={handleSaveSender} disabled={createSender.isPending || updateSenderMut.isPending}>
                    {t("sms.save")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
        </div>
        {sendersLoading ? (
          <div className="flex items-center justify-center p-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : bankSenders && bankSenders!.length > 0 ? (
          <div className="space-y-2">
            {bankSenders!.map((sender) => (
              <div
                key={sender.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-lg bg-background/60 backdrop-blur-sm border border-border/50"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{sender.bankName}</p>
                    <p className="text-xs text-muted-foreground">{sender.senderNumber}</p>
                  </div>
                  {sender.bankCode && (
                    <Badge variant="outline" className="text-xs flex-shrink-0">{sender.bankCode}</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <Badge variant={sender.isActive === 1 ? "default" : "secondary"} className="text-xs">
                    {sender.isActive === 1 ? t("sms.active") : t("sms.inactive")}
                  </Badge>
                  <Switch
                    checked={sender.isActive === 1}
                    onCheckedChange={() => handleToggleSender(sender.id, sender.isActive)}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => handleEditSender(sender)}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive"
                    onClick={() => deleteSender.mutate({ id: sender.id })}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">{t("sms.noBanks")}</p>
        )}
      </div>}

      {/* Delete All Transactions -- dashed border like Budgets unbudgeted section */}
      <div className="rounded-xl border-2 border-dashed border-destructive/30 bg-destructive/5 p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-destructive/10 flex items-center justify-center">
              <Trash2 className="h-4 w-4 text-destructive" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">{t("sms.deleteAll")}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">
                {language === "ar" ? "حذف جميع المعاملات المحفوظة نهائياً" : "Permanently delete all saved transactions"}
              </p>
            </div>
          </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" className="gap-1" disabled={deleteAll.isPending}>
                  {deleteAll.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                  {deleteAll.isPending ? t("sms.deleting") : t("sms.deleteAll")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-destructive" />
                    {t("sms.deleteAll")}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("sms.deleteAllConfirm")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("sms.cancel")}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => deleteAll.mutate()}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {t("sms.deleteAll")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
        </div>
      </div>

      {/* Single Parse Result -- accent border based on success/failure */}
      {lastResult && mode === "sms" && (
        <Card className={`border-l-4 ${lastResult.success ? "border-l-green-500" : "border-l-red-500"}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              {lastResult.success ? (
                <CheckCircle2 className="h-4 w-4" style={{ color: "var(--income)" }} />
              ) : (
                <XCircle className="h-4 w-4" style={{ color: "var(--expense)" }} />
              )}
              <CardTitle className="text-base">{t("sms.parseResult")}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {lastResult.success && lastResult.transaction ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t("field.type")}</p>
                  <Badge
                    variant="secondary"
                    className="gap-1"
                    style={{
                      color: lastResult.transaction.type === "cash_in" ? "var(--income)" : "var(--expense)",
                    }}
                  >
                    {lastResult.transaction.type === "cash_in" ? (
                      <ArrowDownLeft className="h-3 w-3" />
                    ) : (
                      <ArrowUpRight className="h-3 w-3" />
                    )}
                    {lastResult.transaction.type === "cash_in" ? t("field.cashIn") : t("field.cashOut")}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t("field.amount")}</p>
                  <p className="text-lg font-bold text-foreground">{lastResult.transaction.amount.toFixed(3)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t("field.merchant")}</p>
                  <p className="text-sm font-medium text-foreground">{lastResult.transaction.merchant}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t("field.category")}</p>
                  <Badge variant="outline">
                    {getCategoryLabel(lastResult.transaction.category as string)}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t("field.date")}</p>
                  <p className="text-sm text-foreground">
                    {new Date(lastResult.transaction.transactionDate).toLocaleDateString(language === "ar" ? "ar-KW" : "en-US")}
                  </p>
                </div>
                {lastResult.transaction.balance != null && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t("field.balance")}</p>
                    <p className="text-sm text-foreground">{lastResult.transaction.balance.toFixed(3)}</p>
                  </div>
                )}
                {lastResult.transaction.bankSource && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t("field.bankSource")}</p>
                    <Badge variant="secondary" className="gap-1">
                      <Building2 className="h-3 w-3" />
                      {lastResult.transaction.bankSource}
                    </Badge>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10">
                <XCircle className="h-5 w-5 text-destructive" />
                <div>
                  <p className="text-sm font-medium text-foreground">{t("sms.couldNotParse")}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("sms.couldNotParseDesc")}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bulk Parse Result */}
      {bulkResult && mode === "sms" && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t("sms.bulkResults")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                <CheckCircle2 className="h-4 w-4" style={{ color: "var(--income)" }} />
                <span className="text-sm font-medium text-foreground">{bulkResult.parsedCount} {t("sms.parsed")}</span>
              </div>
              {bulkResult.failedCount > 0 && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10">
                  <XCircle className="h-4 w-4 text-destructive" />
                  <span className="text-sm font-medium text-foreground">{bulkResult.failedCount} {t("sms.failed")}</span>
                </div>
              )}
              {bulkResult.duplicateCount > 0 && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">{bulkResult.duplicateCount} {t("sms.duplicates")}</span>
                </div>
              )}
            </div>
            {bulkResult.failedMessages && bulkResult.failedMessages.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">{t("sms.failedMessages")}</p>
                {bulkResult.failedMessages.map((msg, i) => (
                  <div key={i} className="p-3 rounded-lg bg-muted/50 text-xs font-mono text-muted-foreground" dir="auto">
                    {msg.substring(0, 200)}
                    {msg.length > 200 ? "..." : ""}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Image Parse Result */}
      {imageResult && mode === "image" && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t("sms.imageResults")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-4">
              {imageResult.success && (
                <>
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                    <CheckCircle2 className="h-4 w-4" style={{ color: "var(--income)" }} />
                    <span className="text-sm font-medium text-foreground">{imageResult.parsedCount} {t("sms.parsed")}</span>
                  </div>
                  {imageResult.failedCount > 0 && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10">
                      <XCircle className="h-4 w-4 text-destructive" />
                      <span className="text-sm font-medium text-foreground">{imageResult.failedCount} {t("sms.failed")}</span>
                    </div>
                  )}
                  {imageResult.duplicateCount > 0 && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                      <Shield className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">{imageResult.duplicateCount} {t("sms.duplicates")}</span>
                    </div>
                  )}
                </>
              )}
            </div>
            {imageResult.bankSender && (
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-foreground">
                  {t("sms.bankDetected")} {imageResult.bankSender}
                </span>
              </div>
            )}
            {imageResult.success && imageResult.extractedMessages && imageResult.extractedMessages.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">{t("sms.extractedMessages")}</p>
                {imageResult.extractedMessages.map((msg, i) => (
                  <div key={i} className="p-3 rounded-lg bg-muted/50 text-xs font-mono text-muted-foreground" dir="auto">
                    {msg}
                  </div>
                ))}
              </div>
            )}
            {!imageResult.success && (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10">
                <XCircle className="h-5 w-5 text-destructive" />
                <div>
                  <p className="text-sm font-medium text-foreground">{t("sms.couldNotParse")}</p>
                  <p className="text-xs text-muted-foreground mt-1">{imageResult.error}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* PDF Parse Result */}
      {pdfResult && mode === "pdf" && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" />
              {t("sms.pdfResults")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {pdfResult.success ? (
              <>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                    <CheckCircle2 className="h-4 w-4" style={{ color: "var(--income)" }} />
                    <span className="text-sm font-medium text-foreground">{pdfResult.parsedCount} {t("sms.parsed")}</span>
                  </div>
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">{pdfResult.totalPages} {t("sms.pdfPages")}</span>
                  </div>
                  {pdfResult.duplicateCount > 0 && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                      <Shield className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">{pdfResult.duplicateCount} {t("sms.duplicates")}</span>
                    </div>
                  )}
                  {pdfResult.totalExtracted && pdfResult.totalExtracted !== pdfResult.parsedCount && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                      <span className="text-sm text-muted-foreground">{pdfResult.totalExtracted} {t("sms.pdfTransactions")}</span>
                    </div>
                  )}
                  {pdfResult.controlStatus === "unavailable" && <span className="text-sm text-muted-foreground">{t("sms.pdfControlUnavailable")}</span>}
                  {pdfResult.controlStatus === "forced_mismatch" && <span className="text-sm text-amber-700 dark:text-amber-300">{t("sms.pdfControlForcedMismatch")}</span>}
                  {pdfResult.controlStatus === "passed" && <span className="text-sm text-muted-foreground">{t("sms.pdfControlPassed")}</span>}
                </div>
                {(pdfResult.bankName || pdfResult.customerName || pdfResult.statementPeriod) && (
                  <div className="p-4 rounded-lg bg-muted/30 space-y-2">
                    {pdfResult.bankName && (
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-primary" />
                        <span className="text-sm"><strong>{t("sms.pdfBank")}</strong> {pdfResult.bankName}</span>
                      </div>
                    )}
                    {pdfResult.customerName && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm"><strong>{t("sms.pdfCustomer")}</strong> {pdfResult.customerName}</span>
                      </div>
                    )}
                    {pdfResult.statementPeriod && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm"><strong>{t("sms.pdfPeriod")}</strong> {pdfResult.statementPeriod}</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10">
                <XCircle className="h-5 w-5 text-destructive" />
                <div>
                  <p className="text-sm font-medium text-foreground">{t("sms.couldNotParse")}</p>
                  <p className="text-xs text-muted-foreground mt-1">{pdfResult.error}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Example SMS Formats */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquareText className="h-4 w-4" />
            {t("sms.supportedFormats")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              // English NBK examples
              "Your account 0620 has been debited with KWD 4.619 Account to Account Transfer on 2026-03-09 15:45:27 . Your remaining balance is KWD 5,346.056",
              "Your account 0620 has been credited with KWD 88.000 by a generated NBK Quick Pay on 2026-03-04 20:13:27 .Your remaining balance is KWD 5,574.675 .",
              // Arabic KFH examples
              "راتب - إيداع 602.982 د.ك حسابك xx524 رصيدك -735.000 د.ك",
              "دفع نقاط بيع PRET A MANGER - خصم 2.600 د.ك حسابك xx112 رصيدك 67.568 د.ك",
              "تسوية  - خصم 602.982 د.ك حسابك xx524 رصيدك -1337.982 د.ك",
              "دفع نقاط بيع BAQALA BODEGA - خصم 4.740 د.ك حسابك xx112 رصيدك 31.468 د.ك",
            ].map((example, i) => (
              <button
                key={i}
                onClick={() => {
                  setSmsText(example);
                  setMode("sms");
                }}
                className="w-full text-left p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors text-xs font-mono text-muted-foreground"
                dir="auto"
              >
                {example}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Success Popup Dialog */}
      <Dialog open={successPopup?.open ?? false} onOpenChange={(open) => { if (!open) setSuccessPopup(null); }}>
        <DialogContent className="sm:max-w-md max-h-[75vh] sm:max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-primary" />
            </div>
            <DialogTitle className="text-center text-xl">
              {successPopup?.title}
            </DialogTitle>
          </DialogHeader>
          <p className="text-center text-sm text-muted-foreground px-4">
            {successPopup?.description}
          </p>

          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setSuccessPopup(null)}
            >
              {t("success.uploadMore")}
            </Button>
            <Button
              className="flex-1"
              onClick={() => {
                setSuccessPopup(null);
                setLocation("/");
              }}
            >
              {t("success.done")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>



      {/* Balance Chain Conflict Resolution Dialog */}
      {balanceChainConflict?.open && (
        <Dialog open={true} onOpenChange={() => setBalanceChainConflict(null)}>
          <DialogContent className="max-w-lg max-h-[75vh] sm:max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-5 w-5" />
                {language === "ar" ? "تعارض في سلسلة الأرصدة" : "Balance Chain Conflict"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {language === "ar"
                  ? "هذه المعاملات لا تتوافق مع الأرصدة المسجلة لهذا الحساب. عادة ما يعني هذا أن الكشف ينتمي لحساب مختلف."
                  : balanceChainConflict.message}
              </p>

              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <p className="text-sm font-medium mb-2">
                  {language === "ar"
                    ? `تم اكتشاف ${balanceChainConflict.totalBreaks} انقطاع في سلسلة الأرصدة`
                    : `${balanceChainConflict.totalBreaks} balance break(s) detected`}
                </p>
                <div className="space-y-1">
                  {balanceChainConflict.breaks.slice(0, 3).map((b, i) => (
                    <div key={i} className="text-xs text-muted-foreground flex justify-between">
                      <span>{new Date(b.transactionDate).toLocaleDateString()}</span>
                      <span className="text-red-600 font-mono">
                        {language === "ar" ? "فرق" : "Gap"}: KWD {b.breakSize.toFixed(3)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-sm">
                {language === "ar"
                  ? `${balanceChainConflict.pendingCount} معاملة في انتظار الاستيراد`
                  : `${balanceChainConflict.pendingCount} transactions pending import`}
              </p>

              <div className="flex flex-col gap-2">
                <Button
                  className="w-full"
                  onClick={() => {
                    setPdfProcessing(true);
                    setBalanceChainConflict(null);
                    parsePDF.mutate({
                      base64: balanceChainConflict.base64,
                      fileName: balanceChainConflict.fileName,
                      bankSource: balanceChainConflict.bankSource,
                      conflictResolution: 'new_account',
                    });
                  }}
                >
                  {language === "ar" ? "استيراد كحساب جديد منفصل (موصى به)" : "Import as New Separate Account (Recommended)"}
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setPdfProcessing(true);
                    setBalanceChainConflict(null);
                    parsePDF.mutate({
                      base64: balanceChainConflict.base64,
                      fileName: balanceChainConflict.fileName,
                      bankSource: balanceChainConflict.bankSource,
                      conflictResolution: 'force',
                    });
                  }}
                >
                  {language === "ar" ? "استيراد في هذا الحساب على أي حال" : "Import Into This Account Anyway"}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => setBalanceChainConflict(null)}
                >
                  {language === "ar" ? "إلغاء" : "Cancel"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Page-level raw-text row count detected a missing LLM extraction row. */}
      {pageShortfall?.open && (
        <Dialog open={true} onOpenChange={() => setPageShortfall(null)}>
          <DialogContent className="max-w-md max-h-[75vh] overflow-y-auto z-[60]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                {language === "ar" ? "اكتشاف صفوف مفقودة" : "Missing Statement Rows Detected"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm">
              <p className="text-muted-foreground">
                {language === "ar"
                  ? "عدد صفوف الكشف لا يتطابق مع الصفوف التي استخرجها النظام. لم يتم استيراد أي معاملة."
                  : "The statement text contains more dated transaction rows than the extraction returned. No transactions were imported."}
              </p>
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 space-y-2">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <span className="text-muted-foreground">{language === "ar" ? "الصفحات المعالجة:" : "Pages processed:"}</span><span>{pageShortfall.pagesProcessed}</span>
                  <span className="text-muted-foreground">{language === "ar" ? "الصفوف الموجودة:" : "Rows found:"}</span><span>{pageShortfall.rowsFound}</span>
                  <span className="text-muted-foreground">{language === "ar" ? "الصفوف المتوقعة:" : "Rows expected:"}</span><span>{pageShortfall.rowsExpected}</span>
                </div>
                <div className="border-t border-border pt-2 space-y-1">
                  {pageShortfall.shortfalls.map((shortfall) => (
                    <div key={shortfall.pageNumber} className="flex justify-between text-xs">
                      <span>{language === "ar" ? `الصفحة ${shortfall.pageNumber}` : `Page ${shortfall.pageNumber}`}</span>
                      <span className="font-medium text-destructive">{language === "ar" ? `${shortfall.extractedRows}/${shortfall.expectedRows} صف` : `${shortfall.extractedRows}/${shortfall.expectedRows} rows`}</span>
                    </div>
                  ))}
                </div>
                {(pageShortfall.valueUnaccounted.debits > 0 || pageShortfall.valueUnaccounted.credits > 0) && (
                  <p className="border-t border-border pt-2 text-xs text-destructive">
                    {language === "ar" ? "قيمة غير محسوبة: " : "Value unaccounted for: "}
                    {pageShortfall.valueUnaccounted.debits > 0 && `${language === "ar" ? "مدين" : "debits"} KWD ${pageShortfall.valueUnaccounted.debits.toFixed(3)} `}
                    {pageShortfall.valueUnaccounted.credits > 0 && `${language === "ar" ? "دائن" : "credits"} KWD ${pageShortfall.valueUnaccounted.credits.toFixed(3)}`}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Button className="w-full" onClick={() => {
                  setPdfProcessing(true);
                  parsePDF.mutate({
                    base64: pageShortfall.base64,
                    fileName: pageShortfall.fileName,
                    bankSource: pageShortfall.bankSource,
                    retryCount: pageShortfall.retryCount + 1,
                  });
                  setPageShortfall(null);
                }}>
                  {language === "ar" ? "إعادة محاولة الاستخراج" : "Retry Extraction"}
                </Button>
                <Button variant="outline" className="w-full" onClick={() => {
                  setPdfProcessing(true);
                  parsePDF.mutate({
                    base64: pageShortfall.base64,
                    fileName: pageShortfall.fileName,
                    bankSource: pageShortfall.bankSource,
                    forceImport: true,
                    retryCount: pageShortfall.retryCount,
                  });
                  setPageShortfall(null);
                }}>
                  {language === "ar" ? "استيراد على أي حال (سيتم وضع علامة غير مكتمل)" : "Import Anyway (tag as incomplete)"}
                </Button>
                <Button variant="ghost" className="w-full" onClick={() => setPageShortfall(null)}>
                  {language === "ar" ? "إلغاء" : "Cancel"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Validation Mismatch Dialog */}
      {validationMismatch && (
        <Dialog open={validationMismatch.open} onOpenChange={() => setValidationMismatch(null)}>
          <DialogContent className="max-w-md max-h-[75vh] overflow-y-auto z-[60]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                {language === "ar" ? "تحذير: استخراج غير مكتمل" : "Warning: Incomplete Extraction"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm">
              <p className="text-muted-foreground">
                {language === "ar"
                  ? "المبالغ المستخرجة لا تتطابق مع إجمالي كشف الحساب. بعض المعاملات قد تكون مفقودة."
                  : "The extracted amounts do not match the statement control totals. Some transactions may be missing."}
              </p>

              <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                <div className="font-medium">{language === "ar" ? "التفاصيل:" : "Details:"}</div>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <span className="text-muted-foreground">{language === "ar" ? "الصفحات:" : "Pages:"}</span>
                  <span>{validationMismatch.totalPages}</span>
                  <span className="text-muted-foreground">{language === "ar" ? "المعاملات المستخرجة:" : "Rows extracted:"}</span>
                  <span>{validationMismatch.parsedCount}</span>
                </div>
                <div className="border-t border-border my-2" />
                <div className="grid grid-cols-3 gap-1 text-xs">
                  <span></span>
                  <span className="font-medium text-center">{language === "ar" ? "المتوقع" : "Expected"}</span>
                  <span className="font-medium text-center">{language === "ar" ? "المستخرج" : "Extracted"}</span>

                  <span className="text-muted-foreground">{language === "ar" ? "المدين:" : "Debits:"}</span>
                  <span className="text-center">{validationMismatch.expectedDebits.toFixed(3)}</span>
                  <span className="text-center">{validationMismatch.actualDebits.toFixed(3)}</span>

                  <span className="text-muted-foreground">{language === "ar" ? "الدائن:" : "Credits:"}</span>
                  <span className="text-center">{validationMismatch.expectedCredits.toFixed(3)}</span>
                  <span className="text-center">{validationMismatch.actualCredits.toFixed(3)}</span>
                </div>
                {(validationMismatch.missingDebitAmount !== 0 || validationMismatch.missingCreditAmount !== 0) && (
                  <div className="border-t border-border mt-2 pt-2">
                    <div className="text-xs text-destructive font-medium">
                      {language === "ar" ? "المفقود:" : "Missing:"}
                      {validationMismatch.missingDebitAmount > 0 && ` ${language === "ar" ? "مدين" : "Debits"} ${validationMismatch.missingDebitAmount.toFixed(3)} KWD`}
                      {validationMismatch.missingCreditAmount > 0 && ` ${language === "ar" ? "دائن" : "Credits"} ${validationMismatch.missingCreditAmount.toFixed(3)} KWD`}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => {
                    setValidationMismatch(null);
                    // Retry with same file
                    if (pdfFile) {
                      setPdfProcessing(true);
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        const dataUrl = ev.target?.result as string;
                        const base64 = dataUrl.split(",")[1];
                        const bankSource = selectedBankSource ?? detectedBank ?? undefined;
                        parsePDF.mutate({ base64, fileName: pdfFile.name, bankSource, retryCount: validationMismatch.retryCount + 1 });
                      };
                      reader.readAsDataURL(pdfFile);
                    }
                  }}
                  className="w-full"
                >
                  {language === "ar" ? "إعادة المحاولة" : "Retry Extraction"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setValidationMismatch(null);
                    // Force import with what we have
                    if (pdfFile) {
                      setPdfProcessing(true);
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        const dataUrl = ev.target?.result as string;
                        const base64 = dataUrl.split(",")[1];
                        const bankSource = selectedBankSource ?? detectedBank ?? undefined;
                        parsePDF.mutate({
                          base64,
                          fileName: pdfFile.name,
                          bankSource,
                          forceImport: true,
                          retryCount: validationMismatch.retryCount,
                        });
                      };
                      reader.readAsDataURL(pdfFile);
                    }
                  }}
                  className="w-full"
                >
                  {language === "ar" ? "استيراد على أي حال (قد تكون ناقصة)" : "Import Anyway (may be incomplete)"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setValidationMismatch(null)}
                  className="w-full"
                >
                  {language === "ar" ? "إلغاء" : "Cancel"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
